import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Privacy Policy',
  sections: [{
    heading: 'Information We Collect',
    body: 'When you purchase The 21 Day Perimenopause Belly Reset, we collect only the information necessary to process your order, including your name, email address, and payment details. Payment processing is handled by secure third-party providers — we do not store your credit card information.'
  }, {
    heading: 'How We Use Your Information',
    body: 'Your information is used solely to deliver your purchased product, send order confirmations, and provide any necessary customer support related to your purchase. We do not sell, rent, or share your personal information with third parties for marketing purposes.'
  }, {
    heading: 'Cookies',
    body: 'Our website may use cookies to improve your browsing experience and analyze site traffic. You can disable cookies through your browser settings at any time.'
  }, {
    heading: 'Data Security',
    body: 'We take reasonable measures to protect your personal information. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.'
  }, {
    heading: 'Your Rights',
    body: 'You may request access to, correction of, or deletion of your personal data at any time. We will respond to your request within a reasonable timeframe.'
  }, {
    heading: 'Changes to This Policy',
    body: 'We may update this privacy policy from time to time. Any changes will be posted on this page with an updated effective date.'
  }]
};
const termsContent = {
  title: 'Terms of Service',
  sections: [{
    heading: 'Product Description',
    body: 'The 21 Day Perimenopause Belly Reset is a digital PDF product containing educational content about nutrition, movement, and lifestyle habits. It is not a medical program and does not replace professional medical advice, diagnosis, or treatment.'
  }, {
    heading: 'Digital Product Delivery',
    body: 'Upon successful payment, you will receive immediate access to download the PDF. Due to the digital nature of this product, all sales are final. No refunds will be issued once the product has been delivered.'
  }, {
    heading: 'Disclaimer',
    body: 'The information provided in this product is for educational and informational purposes only. Always consult with a qualified healthcare professional before starting any new diet, exercise, or wellness program, especially if you have pre-existing health conditions.'
  }, {
    heading: 'Intellectual Property',
    body: 'All content within The 21 Day Perimenopause Belly Reset is protected by copyright. You may not reproduce, distribute, or share the content without written permission. Your purchase grants you a single-user, non-transferable license for personal use only.'
  }, {
    heading: 'Limitation of Liability',
    body: 'We are not liable for any outcomes resulting from the use of this product. Individual results may vary. By purchasing, you acknowledge that you are responsible for your own health decisions.'
  }, {
    heading: 'Governing Law',
    body: 'These terms are governed by applicable law. We reserve the right to update these terms at any time. Continued use of the product constitutes acceptance of any changes.'
  }]
};
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const content = activeModal === 'privacy' ? privacyContent : termsContent;
  return <>
      <footer className="w-full px-6 py-12 bg-cream-100 text-center border-t border-cream-200" data-id="element-43">
        <div className="max-w-2xl mx-auto text-stone-500 text-sm space-y-4" data-id="element-44">
          <p data-id="element-45">
            Disclaimer: This content is for educational purposes only and is not
            intended to replace professional medical advice, diagnosis, or
            treatment. Always seek the advice of your physician or other
            qualified health provider with any questions you may have regarding
            a medical condition.
          </p>
          <div className="flex items-center justify-center gap-4 pt-2" data-id="element-46">
            <button onClick={() => setActiveModal('privacy')} className="underline underline-offset-2 text-stone-500 hover:text-terracotta transition-colors duration-200" data-id="element-47">
              Privacy Policy
            </button>
            <span className="text-stone-300" data-id="element-48">·</span>
            <button onClick={() => setActiveModal('terms')} className="underline underline-offset-2 text-stone-500 hover:text-terracotta transition-colors duration-200" data-id="element-49">
              Terms of Service
            </button>
          </div>
          <p data-id="element-50">
            © {new Date().getFullYear()} The 21 Day Perimenopause Belly Reset.
            All rights reserved.
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-51">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-800/40 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-52">
            <motion.div initial={{
          opacity: 0,
          y: 12,
          scale: 0.98
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 8,
          scale: 0.98
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} className="relative bg-cream-50 rounded-2xl shadow-2xl max-w-xl w-full max-h-[80vh] overflow-y-auto border border-cream-200" onClick={e => e.stopPropagation()} data-id="element-53">
              <div className="sticky top-0 bg-cream-50 border-b border-cream-200 px-8 py-5 flex items-center justify-between rounded-t-2xl" data-id="element-54">
                <h2 className="font-serif text-2xl text-stone-800" data-id="element-55">
                  {content.title}
                </h2>
                <button onClick={() => setActiveModal(null)} className="p-1.5 rounded-full text-stone-400 hover:text-stone-700 hover:bg-cream-200 transition-colors duration-200" aria-label="Close" data-id="element-56">
                  <X className="w-5 h-5" data-id="element-57" />
                </button>
              </div>

              <div className="px-8 py-6 space-y-6" data-id="element-58">
                {content.sections.map((section, i) => <div key={i} data-id="element-59">
                    <h3 className="font-serif text-lg text-stone-800 mb-2" data-id="element-60">
                      {section.heading}
                    </h3>
                    <p className="text-stone-600 leading-relaxed text-[15px]" data-id="element-61">
                      {section.body}
                    </p>
                  </div>)}
              </div>

              <div className="px-8 py-5 border-t border-cream-200 text-center" data-id="element-62">
                <button onClick={() => setActiveModal(null)} className="text-sm text-stone-500 hover:text-terracotta underline underline-offset-2 transition-colors duration-200" data-id="element-63">
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}